import classnames from 'classnames'
import moment from 'moment'
import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'
import LeaderboardWrapper from '../../../common/leaderboard/LeaderboardWrapper'
import { colorNameToData, getColorNameFromStatus } from '../../../../utility/program-dashboard-helper'
import { selectAllEnrollments } from '../../../../reducers/programDashboardReducer'

import classes from './StatsPanel.module.scss'
import { ReactComponent as GolferHalfIcon } from '../../../../assets/images/common/icons/golfer-half-icon.svg'
import { ReactComponent as FlagCircleIcon } from '../../../../assets/images/common/icons/flag-icon.svg'
import { ReactComponent as InfoCircleIcon } from '../../../../assets/images/common/icons/info-circle-icon.svg'

const Placeholder = () => {
  const { t } = useTranslation()
  const { program } = useSelector(state => state.programDashboard)
  const enrollments = useSelector(selectAllEnrollments)
  const withoutEnrollments = enrollments.length === 0

  const bodyContent = useMemo(() => {
    switch (program?.status) {
      case 'future':
        if (withoutEnrollments) {
          return ({
            text: t('programs.manage.leaderboardWidget.registrationOpensInDays', {
              count: moment(program.registrationStartsAt).diff(moment(), 'days'),
            }),
            link: 'https://intercom.help/op-36/en/articles/11144981-program-and-event-states',
            linkText: t('programs.manage.leaderboardWidget.learnMoreProgramStates'),
          })
        }
        break
      case 'active':
        if (withoutEnrollments) {
          return ({
            text: t('programs.manage.leaderboardWidget.shareMarketText'),
            link: 'https://operation36golf.com/resource-sections/4',
            linkText: t('programs.manage.leaderboardWidget.learnMoreMarketing'),
          })
        } else {
          return ({
            text: t('programs.manage.leaderboardWidget.studentsEarnPointsText'),
            link: 'https://intercom.help/op-36/en/articles/11145133-use-the-op-36-community-leaderboard-to-gamify-your-program',
            linkText: t('programs.manage.leaderboardWidget.learnMoreLeaderboards'),
          })
        }
      case 'draft':
        return ({
          text: t('programs.manage.leaderboardWidget.draftStateText'),
          link: 'https://intercom.help/op-36/en/articles/11144981-program-and-event-states',
          linkText: t('programs.manage.leaderboardWidget.learnMoreProgramStates'),
        })
      case 'completed':
        return ({
          text: t('programs.manage.leaderboardWidget.noActivitiesLoggedText'),
          link: 'https://intercom.help/op-36/en/articles/11145133-use-the-op-36-community-leaderboard-to-gamify-your-program',
          linkText: t('programs.manage.leaderboardWidget.learnMoreLeaderboards'),
        })
      default:
        return null
    }
  }, [program.registrationStartsAt, program.status, withoutEnrollments, t])

  return (
    <div className={classnames('d-flex flex-column align-items-center', classes.placeholder)}>
      <GolferHalfIcon className={classes.icon} />
      <h4 className={classes.title}>
        {withoutEnrollments && program.status !== 'completed'
          ? t('programs.manage.leaderboardWidget.noStudentsEnrolled')
          : t('programs.manage.leaderboardWidget.noActivities')}
      </h4>
      <div className='d-flex flex-column'>
        <span className={classnames('mb-3', classes.text)}>{bodyContent.text}</span>
        <a href={bodyContent.link} target='_blank' className={classes.link}>{bodyContent.linkText}</a>
      </div>
    </div>
  )
}

const LeaderboardWidget = () => {
  const { t } = useTranslation()
  const { program } = useSelector(state => state.programDashboard)
  const enrollments = useSelector(selectAllEnrollments)

  const isFutureStartDate = moment(program?.startDate).isAfter(moment(), 'day')

  const filters = useMemo(() => ({
    startDate: isFutureStartDate ? null : program?.startDate,
    endDate: isFutureStartDate ? new Date() : program?.endDate,
    courseId: program?.id,
  }), [isFutureStartDate, program?.endDate, program?.id, program?.startDate])

  const colorName = useMemo(() => getColorNameFromStatus(program), [program])
  const { tKey: bannerTKey, values: bannerValues } = colorNameToData[colorName].banner.text(program)

  return (
    <section className={classnames('d-flex flex-column', classes.leaderboardWidget)}>
      <div
        className='px-3 py-2 d-flex justify-content-between align-items-center'
        style={{ backgroundColor: colorNameToData[colorName].banner.backgroundColor }}
      >
        <div className='d-flex align-items-center'>
          <FlagCircleIcon width={20} height={20} className='mr-2' style={{ fill: colorNameToData[colorName].banner.color }} />
          <span
            style={{ color: colorNameToData[colorName].banner.color }}
            className={classes.bannerText}
          >
            {t(bannerTKey, bannerValues)}
          </span>
        </div>
        <InfoCircleIcon width={20} height={20} className={classes.infoCircleIcon} />
      </div>
      <div className='d-flex flex-grow-1 flex-column min-h-0'>
        <div className={classnames('p-3 d-flex justify-content-between align-items-center', classes.header)}>
          <h3 className={classes.title}>
            {isFutureStartDate
              ? t('programs.manage.leaderboardWidget.studentsEnrolled')
              : t('programs.manage.leaderboardWidget.programActivityLeaderboard')}
          </h3>
          <span className={classes.timePeriodDetails}>
            {isFutureStartDate
              ? t('programs.manage.leaderboardWidget.allTimeStats')
              : `${moment(program?.startDate)
                .format('MM/DD/YY')} - ${moment(program?.endDate)
                .format('MM/DD/YY')}`}
          </span>
        </div>
        {enrollments.length === 0 ? (
          <div className={classes.placeholderContainer}>
            <Placeholder />
          </div>
        ) : (
          <LeaderboardWrapper
            withDetailedLeaderboard={!isFutureStartDate}
            filters={filters}
            classNames={{
              tabs: 'flex-grow-1',
              tabList: 'mx-3',
            }}
            placeholder={<Placeholder />}
          />
        )}
      </div>
    </section>
  )
}


export default LeaderboardWidget
